import { useEffect, useState } from 'react';
import { Building2, Loader2, Search } from 'lucide-react';
import type { ReportWizardLead, ReportWizardLeads } from './types';
import { CARD, INPUT, SECONDARY } from './ui';
import { leadAddressText } from './leadAddress';

interface Props {
  lead: ReportWizardLead | null;
  leads: ReportWizardLeads;
  onSelect(lead: ReportWizardLead | null): void;
}

export const leadName = (l: ReportWizardLead) => l.companyName ?? l.company ?? '';

export function LeadCard({ lead, leads, onSelect }: Props) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<ReportWizardLead[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!term.trim()) { setResults([]); return undefined; }
    let live = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const found = await leads.search(term);
        if (live) setResults(found);
      } catch {
        if (live) setResults([]);
      } finally {
        if (live) setLoading(false);
      }
    }, 300);
    return () => { live = false; clearTimeout(timer); };
  }, [term, leads]);

  const contact = lead?.primaryContactName ?? lead?.contactName;
  return (
    <div className={CARD}>
      <h3 className="mb-1 text-sm font-bold text-slate-900">Lead <span className="font-normal text-slate-500">(optional)</span></h3>
      {!lead && <p className="mb-3 text-xs text-slate-500">You can draft the report without one. A lead is only needed to save the report to its Drive folder.</p>}
      {lead ? (
        <div className="flex items-center gap-3">
          <Building2 size={20} className="flex-none text-[#007ACC]" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-900">{leadName(lead)}</p>
            {contact && <p className="truncate text-xs text-slate-600">{contact}</p>}
            {leadAddressText(lead.address) && <p className="truncate text-xs text-slate-500">{leadAddressText(lead.address)}</p>}
          </div>
          <button type="button" onClick={() => onSelect(null)} className={SECONDARY}>Change</button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search leads..." className={`${INPUT} pl-9`} />
            {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
          </div>
          {results.length > 0 && (
            <div className="max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200">
              {results.map((l) => (
                <button
                  key={l.leadId}
                  type="button"
                  onClick={() => { onSelect(l); setTerm(''); setResults([]); }}
                  className="min-h-[44px] w-full px-3 py-2.5 text-left active:bg-slate-50"
                >
                  <p className="text-sm font-medium text-slate-900">{leadName(l)}</p>
                  {leadAddressText(l.address) && <p className="text-xs text-slate-500">{leadAddressText(l.address)}</p>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
